import { t } from '@app/lib/i18n';
import { CommandMenuEmptyState } from '@ui';
import {
  type Accessor,
  createEffect,
  createSelector,
  createSignal,
  Show,
} from 'solid-js';
import { type VirtualizerHandle, VList } from 'virtua/solid';
import { CommandItem } from '../CommandItem';
import { createSearchPagination } from '../mobile/create-search-pagination';
import type { CommandMenuItem, PaginationControls } from '../useCommandItems';

const VIRTUAL_ITEM_HEIGHT = 40;
const LIST_PADDING = 16;
const MAX_LIST_HEIGHT = VIRTUAL_ITEM_HEIGHT * 8 + LIST_PADDING;
const EMPTY_STATE_HEIGHT = VIRTUAL_ITEM_HEIGHT * 1.5 + LIST_PADDING;

type CommandResultsProps = {
  items: CommandMenuItem[];
  selectedIndex: number;
  onSelect: (item: CommandMenuItem, openInNewSplit: boolean) => void;
  onItemMouseMove: (index: number) => void;
  scrollSelectedIntoView: boolean;
  pagination?: PaginationControls;
  loading?: boolean;
  label?: string;
};

/** The same result rows, virtualization, and pagination in the menu and embedded search. */
export function CommandResults(props: CommandResultsProps) {
  const [container, setContainer] = createSignal<HTMLDivElement>();
  const height = () =>
    props.items.length
      ? Math.min(
          MAX_LIST_HEIGHT,
          props.items.length * VIRTUAL_ITEM_HEIGHT + LIST_PADDING
        )
      : EMPTY_STATE_HEIGHT;
  return (
    <div
      ref={setContainer}
      role="listbox"
      aria-label={props.label ?? t('commandMenu.searchResults')}
      aria-busy={props.loading || undefined}
      class="overflow-hidden transition-[height] duration-60 ease-out p-2"
      style={{ height: `${height()}px` }}
    >
      <Show
        when={props.items.length}
        fallback={
          <CommandMenuEmptyState>
            <span role="status">
              {props.loading
                ? t('commandMenu.searching')
                : t('commandMenu.noResultsFound')}
            </span>
          </CommandMenuEmptyState>
        }
      >
        <VirtualizedCommandList {...props} container={container} />
      </Show>
    </div>
  );
}

/** Virtualized command list component */
function VirtualizedCommandList(props: {
  container: Accessor<HTMLElement | undefined>;
  items: CommandMenuItem[];
  selectedIndex: number;
  onSelect: (item: CommandMenuItem, openInNewSplit: boolean) => void;
  onItemMouseMove: (index: number) => void;
  scrollSelectedIntoView: boolean;
  pagination?: PaginationControls;
}) {
  const [virtualizerHandle, setVirtualizerHandle] =
    createSignal<VirtualizerHandle>();

  const loadMoreNearEnd = createSearchPagination({
    items: () => props.items,
    container: props.container,
    remaining: () => {
      const handle = virtualizerHandle();
      return handle
        ? handle.scrollSize - handle.scrollOffset - handle.viewportSize
        : undefined;
    },
    enabled: () => !!virtualizerHandle(),
    hasMore: () => props.pagination?.hasMore() ?? false,
    busy: () => props.pagination?.isLoadingMore() ?? false,
    loadMore: () => props.pagination?.loadMore() ?? Promise.resolve(),
  });

  createEffect(() => {
    const index = props.selectedIndex;
    const handle = virtualizerHandle();
    if (
      !props.scrollSelectedIntoView ||
      index < 0 ||
      index >= props.items.length ||
      !handle
    ) {
      return;
    }
    // Skip when all items fit: scrolling would be a no-op at the final
    // container size, but during the height transition the container is
    // briefly clipped and scrollToIndex shifts scrollTop, hiding the search
    // row across category switches.
    if (props.items.length * VIRTUAL_ITEM_HEIGHT <= MAX_LIST_HEIGHT) {
      return;
    }
    handle.scrollToIndex(index, { align: 'nearest' });
  });

  const selector = createSelector(
    () => props.selectedIndex,
    (ndx, selected) => ndx === selected
  );

  return (
    <VList
      ref={setVirtualizerHandle}
      data={props.items}
      style={{ height: '100%' }}
      class="scrollbar-hidden"
      onScroll={loadMoreNearEnd}
    >
      {(item, index) => (
        <CommandItem
          item={item}
          index={index()}
          selected={selector(index())}
          onSelect={props.onSelect}
          onMouseMove={props.onItemMouseMove}
        />
      )}
    </VList>
  );
}
